/* import __COLOCATED_TEMPLATE__ from './publish-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type ArticleContent from 'embercom/models/articles/article-content';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import moment from 'moment-timezone';
import { ScheduledContentUpdateStatus } from 'embercom/services/scheduled-content-update-service';
import type ScheduledContentUpdate from 'embercom/models/content-service/scheduled-content-update';
import { type ScheduleMode } from 'embercom/lib/knowledge-hub/constants';
import type CustomReportsService from 'embercom/services/custom-reports-service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import { type HelpCenterSelection } from './edit/help-center-modal';
import { NEW_SELECTOR } from './side-panel/help-center-selector';

enum ModalStep {
  WhereToPublish = 'where-to-publish',
  WhenToPublish = 'when-to-publish',
}

enum HelpCenterOption {
  InHelpCenter = 'in-help-center',
  UnlistedHelpCenter = 'unlisted-help-center',
}

enum WhenOption {
  PublishNow = 'publish-now',
  Schedule = 'schedule',
}

interface Args {
  activeContent: ArticleContent;
  publishActiveContent: () => Promise<void>;
  // Optional: when omitted (e.g. content-review surface), the modal
  // hides the "Schedule for later" radio.
  scheduleActiveContentUpdate?: (params: {
    mode: ScheduleMode;
    scheduledDate: string;
    timezone?: string;
  }) => Promise<void>;
  onClose: () => void;
}

interface Signature {
  Args: Args;
}

export default class PublishModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare customReportsService: CustomReportsService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare helpCenterService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare scheduledContentUpdateService: $TSFixMe;

  @tracked currentStep: ModalStep = this.initialStep;
  @tracked helpCenterOption: HelpCenterOption = HelpCenterOption.InHelpCenter;
  @tracked whenOption: WhenOption = this.defaultWhenOption;
  @tracked scheduledPublishDate = this.initialScheduledPublishDate;
  @tracked selectedTimezone: string = this.defaultTimezone;
  @tracked today = new Date();
  @tracked isPublishing = false;
  @tracked declare helpCenterSelection: HelpCenterSelection;
  @tracked modalHidden = false;

  // Captured at open-time: <HelpCenterSelector> mutates article.inCollections live, so the getter flips mid-flow.
  needsHelpCenterSelectionAtOpen = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.helpCenterSelection = { selectedTuples: undefined };
    this.needsHelpCenterSelectionAtOpen = this.needsHelpCenterSelection;
  }

  get initialStep(): ModalStep {
    if (this.needsHelpCenterSelection) {
      return ModalStep.WhereToPublish;
    }
    return ModalStep.WhenToPublish;
  }

  get defaultWhenOption(): WhenOption {
    if (this.hasExistingSchedule) {
      return WhenOption.Schedule;
    }
    return WhenOption.PublishNow;
  }

  get isWhereStep(): boolean {
    return this.currentStep === ModalStep.WhereToPublish;
  }

  get isWhenStep(): boolean {
    return this.currentStep === ModalStep.WhenToPublish;
  }

  get article() {
    return this.args.activeContent.article;
  }

  get isScheduleMode(): boolean {
    return this.whenOption === WhenOption.Schedule;
  }

  get needsHelpCenterSelection(): boolean {
    return this.article?.inCollections.length === 0 && this.args.activeContent.isNotPublished;
  }

  get hasSingleHelpCenter(): boolean {
    return this.helpCenterService.allSites.length === 1;
  }

  get shouldShowNotLiveStatus(): boolean {
    return this.hasSingleHelpCenter && !this.helpCenterService.allSites.firstObject.websiteTurnedOn;
  }

  get modalTitle() {
    return this.intl.t('knowledge-hub.content-editor.article-content.modal.publish-article');
  }

  get secondaryButtonLabel() {
    if (this.isWhenStep && this.needsHelpCenterSelectionAtOpen) {
      return this.intl.t('knowledge-hub.content-editor.article-content.publish-modal.back');
    }
    return this.intl.t('knowledge-hub.content-editor.article-content.buttons.cancel');
  }

  get defaultTimezone(): string {
    let pending = this.pendingScheduledContentUpdates[0];
    return pending?.timezone || this.appService.app.timezone;
  }

  get initialScheduledPublishDate(): Date {
    let pending = this.pendingScheduledContentUpdates[0];
    if (pending?.changeAt) {
      return moment.tz(pending.changeAt, this.defaultTimezone).toDate();
    }
    return moment
      .tz(this.appService.app.timezone)
      .add(1, 'day')
      .hour(9)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
  }

  get canSchedule(): boolean {
    return !!this.args.scheduleActiveContentUpdate;
  }

  get pendingScheduledContentUpdates(): ScheduledContentUpdate[] {
    let updates = this.args.activeContent.scheduledContentUpdates ?? [];
    return updates.filter((update) => update.status === ScheduledContentUpdateStatus.PENDING);
  }

  get hasExistingSchedule(): boolean {
    return this.pendingScheduledContentUpdates.length > 0;
  }

  get disableNextButton(): boolean {
    // On "where" step, disable Next when "In a Help Center" is selected but no collection chosen
    return (
      this.helpCenterOption === HelpCenterOption.InHelpCenter &&
      (this.helpCenterSelection.selectedTuples === undefined ||
        this.helpCenterSelection.selectedTuples?.size === 0 ||
        this.helpCenterSelection.selectedTuples?.has(NEW_SELECTOR))
    );
  }

  // The DateAndTimePicker disables past *dates* but not past hours within today,
  // so a same-day selection can still resolve to an instant in the past.
  get isScheduledInPast(): boolean {
    return this.scheduledPublishDate.getTime() <= Date.now();
  }

  get showPastDateError(): boolean {
    return this.isWhenStep && this.isScheduleMode && this.isScheduledInPast;
  }

  get confirmButtonLabel() {
    if (this.isWhereStep) {
      return this.intl.t('knowledge-hub.content-editor.article-content.publish-modal.next');
    }
    if (this.isScheduleMode) {
      return this.intl.t(
        'knowledge-hub.content-editor.article-content.publish-modal.confirm-schedule',
      );
    }
    return this.intl.t(
      'knowledge-hub.content-editor.article-content.publish-modal.confirm-publish',
    );
  }

  get disableConfirmButton(): boolean {
    if (this.isWhereStep) {
      return this.disableNextButton;
    }
    if (this.isScheduleMode && this.isScheduledInPast) {
      return true;
    }
    return false;
  }

  get timezoneOptions(): { text: string; value: string }[] {
    return this.customReportsService
      .timezones()
      .map((tz: { description: string; value: string }) => ({
        text: tz.description,
        value: tz.value,
      }));
  }

  get selectedTimezoneDescription(): string {
    let tz = this.customReportsService
      .timezones()
      .find((t: { value: string; description: string }) => t.value === this.selectedTimezone);
    return tz?.description ?? this.selectedTimezone;
  }

  @action updateHelpCenterSelection(selection: HelpCenterSelection) {
    this.helpCenterSelection = selection;
    let hasRealSelection =
      selection.selectedTuples !== undefined &&
      selection.selectedTuples.size > 0 &&
      !selection.selectedTuples.has(NEW_SELECTOR);
    if (hasRealSelection) {
      this.helpCenterOption = HelpCenterOption.InHelpCenter;
    }
  }

  @action setModalHidden(value: boolean) {
    this.modalHidden = value;
  }

  @action onTimezoneChange(value: string) {
    let wallClock = moment(this.scheduledPublishDate)
      .tz(this.selectedTimezone)
      .format('YYYY-MM-DD HH:mm:ss');
    this.selectedTimezone = value;
    this.scheduledPublishDate = moment.tz(wallClock, 'YYYY-MM-DD HH:mm:ss', value).toDate();
  }

  @action onScheduledPublishDateChange(date: Date) {
    this.scheduledPublishDate = date;
  }

  @action goToNextStep() {
    this.currentStep = ModalStep.WhenToPublish;
  }

  @action goBack() {
    this.currentStep = ModalStep.WhereToPublish;
  }

  @action async confirm() {
    // Step 1: advance to Step 2
    if (this.isWhereStep) {
      this.goToNextStep();
      return;
    }

    if (this.isScheduleMode && this.isScheduledInPast) {
      return;
    }

    // Persist collection choice if "In a Help Center" was selected at Step 1.
    // Applies to both publish-now and schedule paths — scheduling only stores
    // the trigger, so the collection must be saved on the article up front.
    if (
      this.helpCenterOption === HelpCenterOption.InHelpCenter &&
      this.needsHelpCenterSelectionAtOpen
    ) {
      await this.knowledgeHubEditorService.updateHelpCenterAndCollection(this.article);
    }

    // Step 2: execute publish or schedule
    if (this.isScheduleMode && this.args.scheduleActiveContentUpdate) {
      await this.args.scheduleActiveContentUpdate({
        mode: 'publish',
        scheduledDate: this.scheduledPublishDate.toISOString(),
        timezone: this.selectedTimezone,
      });
      this.args.onClose();
    } else {
      this.isPublishing = true;
      await this.args.publishActiveContent();
      this.isPublishing = false;
      this.args.onClose();
    }
  }

  @action cancel() {
    // Secondary button: on step 2 with a step 1, go back
    if (this.isWhenStep && this.needsHelpCenterSelectionAtOpen) {
      this.goBack();
      return;
    }
    this.closeModal();
  }

  @action closeModal() {
    // Clear collections if the help center section was shown
    if (this.needsHelpCenterSelectionAtOpen) {
      this.article?.inCollections.clear();
    }
    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::PublishModal': typeof PublishModal;
    'knowledge-hub/content-editor/article-content/publish-modal': typeof PublishModal;
  }
}
